import React, { useEffect, useState, useRef } from 'react';

type FeedbackType = 'correct' | 'incorrect' | null;

interface FeedbackIcon {
  id: number;
  top: number;
  left: number;
  size: number;
  phase: 'entering' | 'visible' | 'exiting';
}

interface GuessFeedbackOverlayProps {
  type: FeedbackType;
  fullScreen?: boolean;
}

const ICON_COUNT = 16;
const STAGGER_MS = 80;
const HOLD_MS = 400;
const ENTER_MS = 300;
const EXIT_MS = 250;

function generateIcons(): FeedbackIcon[] {
  return Array.from({ length: ICON_COUNT }, (_, i) => ({
    id: i,
    top: 5 + Math.random() * 80,
    left: 5 + Math.random() * 85,
    size: 50 + Math.random() * 70,
    phase: 'entering' as const,
  }));
}

export const GuessFeedbackOverlay: React.FC<GuessFeedbackOverlayProps> = ({ type, fullScreen = false }) => {
  const [icons, setIcons] = useState<FeedbackIcon[]>([]);
  const [visibleCount, setVisibleCount] = useState(0);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    // Clean up previous timeouts
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];

    if (!type) {
      setIcons([]);
      setVisibleCount(0);
      return;
    }

    const newIcons = generateIcons();
    setIcons(newIcons);
    setVisibleCount(0);

    // Stagger icon appearance
    newIcons.forEach((_, i) => {
      const enterTimeout = setTimeout(() => {
        setVisibleCount(prev => prev + 1);
      }, i * STAGGER_MS);
      timeoutsRef.current.push(enterTimeout);
    });

    // Stagger icon exit (FIFO) after all have appeared + hold time
    const allAppearDuration = (ICON_COUNT - 1) * STAGGER_MS + ENTER_MS;
    newIcons.forEach((_, i) => {
      const exitTimeout = setTimeout(() => {
        setIcons(prev =>
          prev.map((icon, idx) =>
            idx === i ? { ...icon, phase: 'exiting' } : icon
          )
        );
      }, allAppearDuration + HOLD_MS + i * STAGGER_MS);
      timeoutsRef.current.push(exitTimeout);
    });

    // Clear all icons after everything has exited
    const totalDuration = allAppearDuration + HOLD_MS + (ICON_COUNT - 1) * STAGGER_MS + EXIT_MS;
    const clearTimeout_ = setTimeout(() => {
      setIcons([]);
      setVisibleCount(0);
    }, totalDuration);
    timeoutsRef.current.push(clearTimeout_);

    return () => {
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, [type]);

  if (icons.length === 0) return null;

  const src = type === 'correct'
    ? '/assets/Green_check.svg'
    : '/assets/Red_X.svg';

  return (
    <div
      className={`${fullScreen ? 'fixed' : 'absolute'} inset-0 pointer-events-none overflow-hidden`}
      style={{ zIndex: fullScreen ? 10 : 0 }}
      aria-hidden="true"
    >
      {icons.slice(0, visibleCount).map((icon) => (
        <img
          key={icon.id}
          src={src}
          alt=""
          className={`absolute ${
            icon.phase === 'exiting'
              ? 'animate-feedback-icon-fade-out'
              : 'animate-feedback-icon-bounce'
          }`}
          style={{
            top: `${icon.top}%`,
            left: `${icon.left}%`,
            width: icon.size,
            height: icon.size,
          }}
        />
      ))}
    </div>
  );
};
